import axios, { AxiosRequestConfig, AxiosResponse, isAxiosError } from 'axios';
import merge from 'deepmerge';
import apiConfig from './apiConfig';
import { AuthProtect, BaseURLSelect } from '@/common/enum';
import type EndPointTypes from 'interfaces/EndPointTypes.ts';
import { logout } from '@/utilities/logout.ts';
import { ensureFreshSession, isSessionUnrecoverable } from '@/store/authToken/authToken.session.ts';
import { Nullable } from 'interfaces/Nullable.ts';

export const genericErrorMessage =
  'We just experienced a technical issue, please wait a few seconds and try again.';

const palmettoBaseURL = import.meta.env.VITE_PALMETTO_ENDPOINT;
const dmaBaseURL = import.meta.env.VITE_DAMAGEASSESSMENT_API_ENDPOINT;

/**
 * Request lifecycle for an authenticated call:
 *
 *   ensureFreshSession ──▶ send ──┬── 2xx ─────────────────────────▶ data
 *                                 └── 401 ──▶ refresh possible? ──┬─ yes ─▶ retry once
 *                                                                 └─ no  ─▶ logout
 *
 * The Palmetto API is LoopBack, which answers 401 for both "no valid session"
 * and "this user may not touch this resource". Only the first is a reason to
 * end the session -- logging out on an authorization denial throws away the
 * user's whole session, their offline queue and their persisted state over a
 * single forbidden request.
 */
async function apiFetch<T>(
  api: EndPointTypes,
  controller?: AbortController
): Promise<{ status: number; data: T }> {
  const params: any = api.params || {}; // eslint-disable-line
  const { headers } = apiConfig;
  const baseURL = api.defaultBaseURL === BaseURLSelect.DMA ? dmaBaseURL : palmettoBaseURL;
  const isProtected = api.authorization === AuthProtect.REQUIRED;

  const send = (token: Nullable<string>): Promise<AxiosResponse<T>> => {
    if (token) {
      params.access_token = token;
    }
    const apiConfigMerge: AxiosRequestConfig = merge.all([
      apiConfig,
      api,
      {
        baseURL,
        headers,
        params,
      },
    ]);
    const axiosCreate = axios.create(apiConfigMerge);
    axiosCreate.interceptors.request.use(
      async function (config) {
        if (controller) {
          config.signal = controller.signal;
        }
        if (token) {
          config.headers.Authorization = token;
        }
        return config;
      },
      function (error) {
        console.log('🚀apiFetch function ERROR: ', error);
        throw Error(error);
      }
    );
    return axiosCreate(api);
  };

  // Renew a near-expired session before the request leaves, so an expiring
  // token produces a silent refresh instead of a 401 the caller has to survive.
  const token = isProtected ? await ensureFreshSession() : null;

  // A request that never reached the server (offline, DNS, abort) carries no
  // response body, so it is reported as a generic technical issue rather than a
  // server error the caller could act on.
  const toClientError = (error: unknown): Error => {
    const response = isAxiosError(error) ? error.response : undefined;
    if (response) return Error(JSON.stringify(response.data));
    console.log('🚀apiFetch ERROR: ', error instanceof Error ? error.message : error);
    return Error(genericErrorMessage);
  };

  try {
    const res = await send(token);
    return { status: res.status, data: res.data };
  } catch (error: unknown) {
    const status = isAxiosError(error) ? error.response?.status : undefined;

    if (status === 401 && isProtected) {
      if (await isSessionUnrecoverable()) {
        logout();
      } else {
        // The session was renewable, so the 401 came from a token that expired
        // between the pre-flight check and the response. Retry once with the
        // refreshed token; a second 401 is an authorization denial and belongs
        // to the caller, not to the session.
        const refreshedToken = await ensureFreshSession();
        if (refreshedToken && refreshedToken !== token) {
          try {
            const retry = await send(refreshedToken);
            return { status: retry.status, data: retry.data };
          } catch (retryError: unknown) {
            throw toClientError(retryError);
          }
        }
      }
    }

    throw toClientError(error);
  }
}

export default apiFetch;
